/**
 * Small, dependency-free dashboard primitives. All visuals use Tailwind
 * `fill-*` / `text-*` utilities backed by the theme CSS variables, so every
 * chart reads correctly in both light and dark mode.
 */
import type { ReactNode } from 'react';

export function StatTile({
  label,
  value,
  sub,
  accent = 'gold',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: 'gold' | 'wine';
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${accent === 'wine' ? 'bg-wine-500' : 'bg-gold-500'}`} />
        <p className="text-[11px] uppercase tracking-[0.18em] text-cream-300/70">{label}</p>
      </div>
      <p className="mt-3 font-display text-4xl leading-none text-cream-50">{value}</p>
      {sub && <p className="mt-2 text-xs text-cream-300/60">{sub}</p>}
    </div>
  );
}

export function Panel({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm uppercase tracking-[0.15em] text-gold-400">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Vertical bar chart. Scrolls horizontally if there are many categories. */
export function BarChart({ data }: { data: { label: string | number; value: number }[] }) {
  if (data.length === 0) return <Empty>No data yet.</Empty>;
  const max = Math.max(...data.map((d) => d.value), 1);
  const barW = 22;
  const gap = 10;
  const h = 150;
  const labelH = 22;
  const width = data.length * (barW + gap) + gap;
  return (
    <div className="overflow-x-auto">
      <svg
        width={width}
        height={h + labelH}
        className="text-cream-300/50"
        role="img"
        aria-label={`Bar chart: ${data.map((d) => `${d.label} ${d.value}`).join(', ')}`}
      >
        {data.map((d, i) => {
          const bh = Math.max((d.value / max) * (h - 10), d.value > 0 ? 3 : 0);
          const x = gap + i * (barW + gap);
          return (
            <g key={i}>
              <rect x={x} y={h - bh} width={barW} height={bh} rx={3} className="fill-wine-500" />
              <text x={x + barW / 2} y={h - bh - 4} textAnchor="middle" className="fill-cream-300/70 text-[9px]">
                {d.value || ''}
              </text>
              <text x={x + barW / 2} y={h + 14} textAnchor="middle" className="fill-cream-300/60 text-[9px]">
                {typeof d.label === 'number' ? `'${String(d.label).slice(2)}` : d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Explicit literal class pairs so Tailwind's content scanner emits them
// (constructing class names at runtime would be silently dropped from the build).
const DONUT_COLORS = [
  { stroke: 'stroke-wine-500', bg: 'bg-wine-500' },
  { stroke: 'stroke-gold-500', bg: 'bg-gold-500' },
  { stroke: 'stroke-wine-300', bg: 'bg-wine-300' },
  { stroke: 'stroke-gold-400', bg: 'bg-gold-400' },
  { stroke: 'stroke-wine-700', bg: 'bg-wine-700' },
  { stroke: 'stroke-cream-300', bg: 'bg-cream-300' },
] as const;
const donutColor = (i: number) => DONUT_COLORS[i % DONUT_COLORS.length] ?? DONUT_COLORS[0];

/** Donut chart + legend. */
export function Donut({ data }: { data: { label: string; value: number }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <Empty>No members yet.</Empty>;
  const r = 52;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg
        viewBox="0 0 140 140"
        className="h-36 w-36 -rotate-90"
        role="img"
        aria-label={`Member levels: ${data.map((d) => `${d.label} ${d.value}`).join(', ')}`}
      >
        <circle cx="70" cy="70" r={r} fill="none" strokeWidth="18" className="stroke-ink-800" />
        {data.map((d, i) => {
          const len = (d.value / total) * c;
          const seg = (
            <circle
              key={i}
              cx="70"
              cy="70"
              r={r}
              fill="none"
              strokeWidth="18"
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              className={donutColor(i).stroke}
            />
          );
          offset += len;
          return seg;
        })}
      </svg>
      <ul className="space-y-1.5 text-sm">
        {data.map((d, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-sm ${donutColor(i).bg}`} />
            <span className="capitalize text-cream-200">{d.label}</span>
            <span className="text-cream-300/50">· {d.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Horizontal labeled bars (ranked list). */
export function RankedBars({
  data,
}: {
  data: { label: ReactNode; meta?: ReactNode; value: number; display: string }[];
}) {
  if (data.length === 0) return <Empty>No scored wines yet.</Empty>;
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <ul className="space-y-3">
      {data.map((d, i) => (
        <li key={i}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="truncate text-sm text-cream-100">{d.label}</span>
            <span className="shrink-0 font-display text-sm text-gold-200">{d.display}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-ink-800">
            <div className="h-full rounded-full bg-gold-500" style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
          {d.meta && <p className="mt-1 text-[11px] text-cream-300/50">{d.meta}</p>}
        </li>
      ))}
    </ul>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-cream-300/50">{children}</p>;
}
